<script setup lang="ts">
import {
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogOverlay,
	AlertDialogPortal,
	AlertDialogRoot,
	AlertDialogTitle,
} from "reka-ui";
import { computed, onUnmounted, ref, useTemplateRef, warn } from "vue";
import type { BaseColor, ClassProps } from "../../lib/base-props";
import { cn } from "../../lib/utils";
import { useLocale } from "../../locale";
import { Button } from "../button";
import { ConfirmState } from "./state";
import type { Confirm, ConfirmAction, ConfirmType } from "./types";

const props = defineProps<ClassProps>();

const { t } = useLocale();

const currentConfirm = ref<Confirm | null>(null);

onUnmounted(
	ConfirmState.register((confirm) => {
		if (open.value) {
			warn(
				"[UI] A confirmation flow is already active. Only one confirmation at a time is supported"
			);
			return;
		}

		open.value = true;
		currentConfirm.value = confirm;

		focusAction();
	})
);

const open = ref(false);

function defaultActionText(action: ConfirmType) {
	switch (action) {
		case "confirm":
			return t("confirm.ok");

		case "yesNo":
			return t("confirm.yes");

		case "delete":
			return t("confirm.delete");
	}

	return "";
}

function defaultActionColor(action: ConfirmType): BaseColor {
	if (action === "delete") {
		return "error";
	}

	return "primary";
}

function defautlCancelText(action: ConfirmType) {
	switch (action) {
		case "confirm":
			return t("confirm.cancel");

		case "yesNo":
			return t("confirm.no");

		case "delete":
			return t("confirm.cancel");
	}

	return "";
}

const action = computed<ConfirmAction | null>(() => {
	if (currentConfirm.value == null) {
		return null;
	}

	const defaultText = defaultActionText(currentConfirm.value.type);
	const defaultColor = defaultActionColor(currentConfirm.value.type);

	return {
		text: defaultText,
		color: defaultColor,
		...(currentConfirm.value.confirm || {}),
	};
});

const actionBtn = useTemplateRef("$actionBtn");

function focusAction() {
	requestAnimationFrame(() => {
		if (currentConfirm.value?.type === "delete") {
			return;
		}

		if (actionBtn.value?.$el instanceof HTMLButtonElement) {
			actionBtn.value.$el.focus();
		}
	});
}

const cancel = computed<ConfirmAction | null>(() => {
	if (currentConfirm.value == null) {
		return null;
	}

	const defaultText = defautlCancelText(currentConfirm.value.type);

	return {
		text: defaultText,
		...(currentConfirm.value.cancel || {}),
	};
});

function onConfirm() {
	if (currentConfirm.value == null) {
		return;
	}

	currentConfirm.value.onConfirm();

	close();
}

function onCancel() {
	if (currentConfirm.value == null) {
		return;
	}

	currentConfirm.value.onCancel();

	close();
}

function close() {
	open.value = false;
	const confirm = currentConfirm.value;

	// Allow the animation to play
	setTimeout(() => {
		// Only set to null if the currentConfirm ref didn't change
		if (currentConfirm.value == confirm) {
			currentConfirm.value = null;
		}
	}, 500);
}
</script>

<template>
	<AlertDialogRoot v-if="currentConfirm != null" :open="open">
		<AlertDialogPortal>
			<AlertDialogOverlay class="dialog-overlay" />
			<AlertDialogContent
				:class="cn('dialog min-w-9/10 sm:min-w-[unset] sm:max-w-lg', props.class)"
			>
				<div class="flex flex-col gap-3 text-center sm:text-left">
					<AlertDialogTitle class="text-lg font-semibold">
						{{ currentConfirm.title }}
					</AlertDialogTitle>
					<AlertDialogDescription class="text-muted text-sm">
						{{ currentConfirm.description }}
					</AlertDialogDescription>
				</div>

				<div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end mt-3">
					<AlertDialogCancel v-if="cancel" asChild>
						<Button
							showFocus
							:class="cancel.classes"
							:style="cancel.css"
							:color="cancel.color"
							@click="onCancel"
						>
							{{ cancel.text }}
						</Button>
					</AlertDialogCancel>
					<AlertDialogAction v-if="action" asChild>
						<Button
							ref="$actionBtn"
							showFocus
							:class="action.classes"
							:style="action.css"
							:color="action.color"
							@click="onConfirm"
						>
							{{ action.text }}
						</Button>
					</AlertDialogAction>
				</div>
			</AlertDialogContent>
		</AlertDialogPortal>
	</AlertDialogRoot>
</template>
